import { Link } from '@mui/material';
import {
  DataGridPremiumProps,
  GridCellParams,
  GridColDef,
} from '@mui/x-data-grid-premium';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AttachmentEditCellComponent } from '~/components/SolaForceDataGrid';
import {
  UseDataGridCRUDValidationOutput,
  UseDataGridRowFileChangesState,
} from '~/components/SolaForceDataGrid/hooks';
import { useDataGridCRUDColumns } from '~/components/SolaForceDataGrid/hooks/useDataGridCRUDColumns';
import { conditionalGridColDefs } from '~/components/SolaForceDataGrid/utils';
import { Enum } from '~/data/enums';
import {
  CompanyEquipmentEntry,
  EmployeeEquipmentTableRow,
} from '~/scenes/PeopleModule/components/Tabs/EquipmentTab/EquipmentTable/data';
import { getEmployeeAttachmentUrl } from '~/utils/helper';

export type UseEquipmentTableColumnsProps = {
  selectedEmployeeId: number;
  companyEquipment: CompanyEquipmentEntry[];
  equipmentTypes: Enum[];
  canEdit: boolean;
  canAlwaysEditExpirationDate: boolean;
} & Pick<
  UseDataGridCRUDValidationOutput<EmployeeEquipmentTableRow>,
  'columnsValidation'
> &
  Pick<UseDataGridRowFileChangesState, 'updateFileChanges' | 'fileChanges'>;

export type UseEquipmentTableColumnsOutput = Pick<
  DataGridPremiumProps<EmployeeEquipmentTableRow>,
  'columns' | 'isCellEditable'
>;

export const useEquipmentTableColumns = (
  props: UseEquipmentTableColumnsProps,
): UseEquipmentTableColumnsOutput => {
  const {
    selectedEmployeeId,
    equipmentTypes,
    companyEquipment,
    columnsValidation,
    updateFileChanges,
    fileChanges,
    canEdit,
    canAlwaysEditExpirationDate,
  } = props;

  const { t } = useTranslation();

  const columns: GridColDef<EmployeeEquipmentTableRow>[] = useMemo(() => {
    const dateColumnSizeProps = {
      flex: 0.3,
      minWidth: 130,
    };

    return [
      {
        field: 'type',
        headerName: t('laType'),
        type: 'singleSelect',
        flex: 0.5,
        minWidth: 140,
        valueFormatter: (value) => {
          return (
            equipmentTypes.find((e) => e.code === value)?.name ?? value ?? ''
          );
        },
      },
      {
        field: 'item',
        headerName: t('laEquipmentItem'),
        flex: 0.7,
        minWidth: 180,
      },
      {
        field: 'inventoryNumber',
        headerName: t('laEquipmentNumber'),
        flex: 0.5,
        minWidth: 140,
      },

      {
        field: 'deliveryDate',
        headerName: t('laDeliveryDate'),
        type: 'date',
        editable: true,
        ...dateColumnSizeProps,
      },
      {
        field: 'returnDate',
        headerName: t('laReturnedDate'),
        type: 'date',
        editable: true,
        ...dateColumnSizeProps,
      },

      {
        field: 'info',
        headerName: t('laEquipmentInfo'),
        type: 'string',
        editable: true,
        flex: 1.5,
        minWidth: 200,
      },

      {
        field: 'attachmentName',
        headerName: t('laAttachment'),
        editable: true,
        flex: 1.5,
        minWidth: 200,
        renderCell: ({ value, row }) => {
          return (
            <Link
              href={getEmployeeAttachmentUrl(
                selectedEmployeeId,
                row.attachmentId,
              )}
              target={'_blank'}
            >
              {value}
            </Link>
          );
        },
        renderEditCell: (params) => {
          return (
            <AttachmentEditCellComponent
              params={params}
              onCellFileChange={({ value, params, apiRef }) => {
                updateFileChanges({ id: params.id, file: value });
                apiRef.current.setEditCellValue({
                  id: params.id,
                  field: params.field,
                  value: value?.name ?? '',
                });
              }}
              getAttachmentUrl={({ params }) => {
                const { row } = params;
                if (
                  row.attachmentId >= 0 &&
                  !fileChanges.some((fc) => fc.id === row.id)
                ) {
                  return getEmployeeAttachmentUrl(
                    selectedEmployeeId,
                    row.attachmentId,
                  );
                }
                return undefined;
              }}
            />
          );
        },
      },

      {
        field: 'expirationDate',
        headerName: t('laExpires'),
        type: 'date',
        editable: true,
        ...dateColumnSizeProps,
      },

      ...conditionalGridColDefs<EmployeeEquipmentTableRow>(canEdit, {
        field: 'actions',
        type: 'actions',
        headerName: t('Actions'),
        width: 90,
        cellClassName: 'actions',
      }),
    ];
  }, [
    t,
    equipmentTypes,
    fileChanges,
    updateFileChanges,
    selectedEmployeeId,
    canEdit,
  ]);

  const isCellEditable: DataGridPremiumProps['isCellEditable'] = useCallback(
    (params: GridCellParams): boolean => {
      const { field, row } = params;

      // Editable if company equipment has no expiration date or special role access
      if (field === 'expirationDate') {
        const foundCompanyEquipment = companyEquipment.find(
          (ce) => ce.id === row.companyEquipmentId,
        );
        const noExpirationDate =
          typeof foundCompanyEquipment?.expirationDate === 'undefined';

        return canAlwaysEditExpirationDate || noExpirationDate;
      }

      // Editable by column config
      return !!columns.find((c) => c.field === field)?.editable;
    },
    [columns, companyEquipment, canAlwaysEditExpirationDate],
  );

  const crudColumnProps = useDataGridCRUDColumns({
    columns,
    columnsValidation,
  });

  return {
    ...crudColumnProps,
    isCellEditable,
  };
};
